import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import newsletterService, { NewsletterSubscriber, NewsletterStats } from '../../services/newsletterService';

type StatusFilter = 'ALL' | 'SUBSCRIBED' | 'UNSUBSCRIBED' | 'BOUNCED';

const AdminNewsletterManagerPage: React.FC = () => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [stats, setStats] = useState<NewsletterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState<NewsletterSubscriber | null>(null);

  // Forms
  const [addForm, setAddForm] = useState({
    email: '',
    tags: '',
  });

  const [tagsForm, setTagsForm] = useState({
    tags: '',
  });

  const [syncService, setSyncService] = useState<'mailchimp' | 'sendgrid' | 'convertkit'>('mailchimp');

  useEffect(() => {
    if (selectedCreatorId) {
      loadData();
    }
  }, [selectedCreatorId, page, statusFilter]);

  const loadData = async () => {
    if (!selectedCreatorId) return;

    try {
      setLoading(true);
      const filters = {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        page,
        limit: 20,
      };

      const [subscribersData, statsData] = await Promise.all([
        newsletterService.getSubscribers(selectedCreatorId, filters),
        newsletterService.getStats(selectedCreatorId),
      ]);

      setSubscribers(subscribersData.subscribers);
      setTotalPages(Math.ceil(subscribersData.total / subscribersData.limit));
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load newsletter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCreatorId) return;

    try {
      const tags = addForm.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t);

      await newsletterService.subscribe(selectedCreatorId, addForm.email, tags);
      alert('Subscriber added successfully!');
      setShowAddModal(false);
      setAddForm({ email: '', tags: '' });
      loadData();
    } catch (error) {
      console.error('Failed to add subscriber:', error);
      alert('Failed to add subscriber. Please try again.');
    }
  };

  const handleUnsubscribe = async (subscriber: NewsletterSubscriber) => {
    if (!confirm(`Are you sure you want to unsubscribe ${subscriber.email}?`)) return;

    try {
      await newsletterService.unsubscribe(subscriber.email, subscriber.creatorProfileId);
      loadData();
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      alert('Failed to unsubscribe.');
    }
  };

  const handleUpdateTags = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubscriber) return;

    try {
      const tags = tagsForm.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t);

      await newsletterService.updateSubscriberTags(selectedSubscriber.id, tags);
      alert('Tags updated successfully!');
      setShowTagsModal(false);
      setTagsForm({ tags: '' });
      setSelectedSubscriber(null);
      loadData();
    } catch (error) {
      console.error('Failed to update tags:', error);
      alert('Failed to update tags.');
    }
  };

  const openTagsModal = (subscriber: NewsletterSubscriber) => {
    setSelectedSubscriber(subscriber);
    setTagsForm({ tags: subscriber.tags.join(', ') });
    setShowTagsModal(true);
  };

  const handleExport = async () => {
    if (!selectedCreatorId) return;

    try {
      const filters = {
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      };

      const blob = await newsletterService.exportSubscribers(selectedCreatorId, filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter-subscribers-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowExportModal(false);
    } catch (error) {
      console.error('Failed to export subscribers:', error);
      alert('Failed to export subscribers.');
    }
  };

  const handleSync = async () => {
    if (!selectedCreatorId) return;

    try {
      const result = await newsletterService.syncToExternalService(selectedCreatorId, syncService);
      alert(`Successfully synced ${result.synced} subscribers to ${syncService}!`);
      setShowSyncModal(false);
    } catch (error) {
      console.error('Failed to sync:', error);
      alert('Failed to sync to external service.');
    }
  };

  const filteredSubscribers = subscribers.filter((sub) => {
    const search = searchTerm.toLowerCase();
    return sub.email?.toLowerCase().includes(search) || sub.tags?.some((tag) => tag.toLowerCase().includes(search));
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      SUBSCRIBED: <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-700">Subscribed</span>,
      UNSUBSCRIBED: <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Unsubscribed</span>,
      BOUNCED: <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-700">Bounced</span>,
    };
    return badges[status as keyof typeof badges] || <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">{status}</span>;
  };

  if (!selectedCreatorId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <EnvelopeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Newsletter Manager</h2>
            <p className="text-gray-600 mb-6">Select a creator to manage their newsletter subscribers</p>
            <div className="max-w-md mx-auto">
              <input
                type="text"
                placeholder="Enter Creator Profile ID..."
                value={selectedCreatorId}
                onChange={(e) => setSelectedCreatorId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading newsletter data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Newsletter Manager</h1>
          <p className="text-gray-600 mt-1">Manage subscribers for creator: {selectedCreatorId}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowExportModal(true)} variant="outline" className="flex items-center gap-2">
            <ArrowDownTrayIcon className="w-5 h-5" />
            Export
          </Button>
          <Button onClick={() => setShowSyncModal(true)} variant="outline" className="flex items-center gap-2">
            <CloudArrowUpIcon className="w-5 h-5" />
            Sync
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Add Subscriber
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Subscribed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.subscribed}</p>
                </div>
                <UserGroupIcon className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unsubscribed</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.unsubscribed}</p>
                </div>
                <UserGroupIcon className="w-8 h-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bounced</p>
                  <p className="text-2xl font-bold text-red-600">{stats.bounced}</p>
                </div>
                <UserGroupIcon className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Statuses</option>
              <option value="SUBSCRIBED">Subscribed</option>
              <option value="UNSUBSCRIBED">Unsubscribed</option>
              <option value="BOUNCED">Bounced</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Subscribers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscribed At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">{subscriber.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {subscriber.tags && subscriber.tags.length > 0 ? (
                            subscriber.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">No tags</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(subscriber.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {new Date(subscriber.subscribedAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => openTagsModal(subscriber)}
                            size="sm"
                            variant="ghost"
                            title="Edit tags"
                          >
                            <TagIcon className="w-4 h-4" />
                          </Button>
                          {subscriber.status === 'SUBSCRIBED' && (
                            <Button
                              onClick={() => handleUnsubscribe(subscriber)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-800"
                              title="Unsubscribe"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Subscriber Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add Subscriber</h2>
            <form onSubmit={handleAddSubscriber} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  placeholder="subscriber@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (Optional)</label>
                <input
                  type="text"
                  value={addForm.tags}
                  onChange={(e) => setAddForm({ ...addForm, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="tag1, tag2, tag3"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated tags</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  Add Subscriber
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddForm({ email: '', tags: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tags Modal */}
      {showTagsModal && selectedSubscriber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Subscriber Tags</h2>
            <p className="text-sm text-gray-600 mb-4">
              Editing tags for: <span className="font-medium">{selectedSubscriber.email}</span>
            </p>
            <form onSubmit={handleUpdateTags} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  value={tagsForm.tags}
                  onChange={(e) => setTagsForm({ ...tagsForm, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="tag1, tag2, tag3"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated tags</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  Update Tags
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowTagsModal(false);
                    setTagsForm({ tags: '' });
                    setSelectedSubscriber(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Export Subscribers</h2>
            <p className="text-sm text-gray-600 mb-6">
              Export subscribers to CSV file. Current filter: <span className="font-medium">{statusFilter}</span>
            </p>
            <div className="flex gap-3">
              <Button onClick={handleExport} className="flex-1 flex items-center justify-center gap-2">
                <ArrowDownTrayIcon className="w-5 h-5" />
                Export to CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowExportModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Sync to External Service</h2>
            <p className="text-sm text-gray-600 mb-4">
              Sync subscribers to an external email service provider
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Service</label>
                <select
                  value={syncService}
                  onChange={(e) => setSyncService(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="mailchimp">Mailchimp</option>
                  <option value="sendgrid">SendGrid</option>
                  <option value="convertkit">ConvertKit</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSync} className="flex-1 flex items-center justify-center gap-2">
                  <CloudArrowUpIcon className="w-5 h-5" />
                  Sync to {syncService}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSyncModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletterManagerPage;
